//React Hooks
import { useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";


//MUI Components
import {
  Box,
  Button,
  Typography,
  Chip,
  Tabs,
  Tab,
  Stack,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Modal
} from "@mui/material";


//MUI Icons
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";


//Custom Components
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import CommonEditDialog from "../../popup-components/CommonEditPopup";
import { addToaddReview, fmTothreatModelling, secRecommendations } from "../../../services/webServices";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { SecurityLoaderStateContext } from "../../main-pages/SecurityTestingPage";
import CommonCopy from "../../custom-components/CommonCopy";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";
import { ReactComponent as DeleteIcon } from "../../../images/SecurityDeleteIcon.svg";
import { SecurityFlowDataContext } from "../../main-pages/SecurityTestingPage";


const IdentifiedFunctionalModules = () => {

  //useContext
  const { setLoadingState } = useContext(LoadingModalContext);
  const { securityLoaderState, setSecurityLoaderState } = useContext(SecurityLoaderStateContext);
  const { funcMod, securityReq, archDesign, brd } = useContext(SecurityFlowDataContext);

  //State Variables for updated output values
  const [funcModData, setFuncModData] = useState([]);
  const [securityReqData, setSecurityReqData] = useState([]);
  const [archDesignData, setArchDesignData] = useState([]);
  const [brdData, setBrdData] = useState([]);
  const [parsed, setParsed] = useState({});
  const [archDesignDescription, setArchDesignDescription] = useState([]);

  //State variables for System Architecture Diagram
  const [pngData, setPngData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  //State Variables for Edit functionality
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorE2, setAnchorE2] = useState(null);
  const [anchorE3, setAnchorE3] = useState(null);
  const [openFuncModEdit, setOpenFuncModEdit] = useState(false);
  const [openSecReqEdit, setOpenSecReqEdit] = useState(false);
  const [openArchDescEdit, setOpenArchDescEdit] = useState(false);
  const [selectedFuncMod, setSelectedFuncMod] = useState(null);
  const [selectedSecReq, setSelectedSecReq] = useState(null);
  const [selectedArchDesc, setSelectedArchDesc] = useState(null);
  const [selectedfuncModuleIndex, setSelectedFuncModuleIndex] = useState(null);
  const [selectedSecReqIndex, setSelectedSecReqIndex] = useState(null);
  const [selectedArchDescIndex, setSelectedArchDescIndex] = useState(null);

  //State Variables for Download
  const [downloadEachTabData, setDownloadEachTabData] = useState(null);
  const [downloadETDOpen, setDownloadETDOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadBRDOpen, setDownloadBRDOpen] = useState(false);

  //Other State Variables
  const [tabValue, setTabValue] = useState(0);
  const [activeThumb, setActiveThumb] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  //For 'Download All' option
  const hrForPdf = "_".repeat(68);
  const hrForWord = "_".repeat(98);

  //Navigation and Routing
  const location = useLocation();
  const navigate = useNavigate();

  //Scrolling reference
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [tabValue]);


  //transforming and updating the outputs
  useEffect(() => {
    const funcModRaw = location.state?.funcMod || funcMod;
    const secReqRaw = location.state?.securityReq || securityReq;
    const archRaw = location.state?.archDesign || archDesign;
    const brdRaw = location.state?.brd || brd;
    // Functional Modules
    if (funcModRaw) {
      const keys = Object.keys(funcModRaw);
      if (keys.length > 0) {
        const itemCount = funcModRaw[keys[0]].length;
        const transformedData = Array.from({ length: itemCount }, (_, index) => {
          const item = {};
          keys.forEach((key) => {
            item[key] = funcModRaw[key][index];
          });
          return item;
        });
        setFuncModData(transformedData);
      } else {
        setFuncModData([]);
      }
    }
    
    // Security Requirements
    if (secReqRaw) {
      setSecurityReqData(secReqRaw);
    }

    // Architecture Design
    if (archRaw) {
      setArchDesignData(archRaw);
    } else {
      setArchDesignData([]);
    }
    // BRD
    if (brdRaw) {
      setBrdData(brdRaw);
    } else {
      setBrdData("");
    }
  }, [location.state, funcMod, securityReq, archDesign, brd]);


  //Saving Image and Description seperately
  useEffect(() => {
    const archDesc = archDesignData?.parsedArchitectureDesign?.["Accompanying Description"] || [];
    setArchDesignDescription(archDesc)
    const convertAndSet = async () => {
      if (archDesignData?.flowchartResponse) {
        const imageData = await svgToPngBase64(archDesignData.flowchartResponse);
        setPngData(imageData);
      }
    };
    convertAndSet();
  }, [archDesignData]);



  //formatting brd - word, pdf
  function stringifyItem(item) {
    if (typeof item === "string") return item.trim();
    if (Array.isArray(item)) return item.map(stringifyItem).join(", ");
    if (typeof item === "object") {
      return Object.entries(item)
        .map(([k, v]) => `${k}: ${stringifyItem(v)}`)
        .join("\n");
    }
    return String(item);
  }
  function formatSection(section) {
    if (!section) return "";
    if (Array.isArray(section)) {
      return section.map(item => `${stringifyItem(item)}`).join("\n");
    }
    if (typeof section === "object") {
      return Object.entries(section)
        .map(([key, value]) => {
          // detect numbered headings like "1.Objectives"
          const numberedHeading = /^\d+\./.test(key);

          if (Array.isArray(value)) {
            // Array under heading → bullet list
            return `${numberedHeading ? key : `${key}`}:\n${value
              .map(v => `${stringifyItem(v)}`)
              .join("\n")}`;
          } else if (typeof value === "object") {
            const isFlatDict = Object.values(value).every(v => typeof v === "string");
            if (isFlatDict) {
              return `${numberedHeading ? key : `${key}`}:\n${Object.entries(value)
                .map(([k, v]) => `${k}: ${v}`)
                .join("\n")}`;
            }
            return `${numberedHeading ? key : `${key}`}:\n${formatSection(value)}`;
          } else {
            // single string under heading
            return `${numberedHeading ? key : `${key}`}: ${stringifyItem(value)}`;
          }
        })
        // add blank line between sections
        .join("\n\n");
    }

    return String(section);
  }



  //Formatting Functional Modules for Word and Pdf
  const functionalModulesToDownload = funcModData.map((item) => ({
    heading: item["Module Name"],
    type: "text",
    data: item["Functional Requirement Description"],
  }));


  //Formatting Security Reqirements for Word and Pdf
  const secRequirementsToDownload = securityReqData.map((item) => {
    const rawData = item["Security Requirements"];
    let pointsArray;
    if (Array.isArray(rawData)) {
      pointsArray = rawData;
    } else if (typeof rawData === "string") {
      pointsArray = rawData
        .split(/^\s*-\s+|\n\s*-\s+/g)
        .map(point => point.trim())
        .filter(point => point.length > 0);
    } else {
      pointsArray = [];
    }

    return {
      heading: item["Module Name"],
      type: "text",
      data: pointsArray.map(point => `• ${point}`).join("\n"),
    };
  });


  //Formatting Architecture Description for Word and Pdf
  const archDescriptionToDownload = {
    heading: "Description",
    type: "text",
    data: archDesignDescription.map((item) => `• ${item}`).join("\n")
  };


  //svg to png conversion for System Architecture diagram
  const svgToPngBase64 = (svgString) => {
    return new Promise((resolve, reject) => {
      const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const pngBase64 = canvas.toDataURL("image/png");
        resolve(pngBase64);
        URL.revokeObjectURL(url);
      };
      img.onerror = reject;
      img.src = url;
    });
  };


  //Handling functions for 'Download Final BRD'
  const handleBRDDownload = () => {
    setDownloadBRDOpen(true);
  };

  //Handling functions for 'Download All'
  const handleDownload = () => {
    setDownloadOpen(true);
  };

  //Handling functions for 'Download'
  const handleEachTabDownload = async () => {
    let serviceResponse = [];
    let wordData = [];
    let serviceName = "";
    switch (tabValue) {
      case 0:
        serviceName = "Functional Modules Document";
        serviceResponse = [
          ...functionalModulesToDownload
        ];
        wordData = [
          ...functionalModulesToDownload
        ];
        break;
      case 1:
        serviceName = "Security Requirements Document";
        serviceResponse = [
          ...secRequirementsToDownload
        ];
        wordData = [
          ...secRequirementsToDownload
        ];
        break;
      case 2:
        serviceName = "System Architecture Document";
        serviceResponse = [
          {
            heading: "System Architecture",
            type: "image",
            data: pngData

          },
          archDescriptionToDownload
        ];
        wordData = [
          {
            heading: "System Architecture",
            type: "image",
            data: pngData?.replace(/^data:image\/png;base64,/, "") || ""

          },
          archDescriptionToDownload
        ];
        break;
      default:
        return;
    }
    setDownloadEachTabData({
      serviceName,
      serviceResponse,
      wordData,
    });
    setDownloadETDOpen(true);
  };


  //Functional Modules - Edit Functionality
  const handleFuncModMenuClick = (event, index) => {
    setAnchorEl(event.currentTarget);
    setSelectedFuncMod(funcModData[index]);
  };
  const handleFuncModMenuClose = () => {
    setAnchorEl(null);
    setSelectedFuncModuleIndex(null);
  };
  const handleFuncModEdit = (item) => {
    setSelectedFuncMod(transformFuncModForEdit(item));
    setOpenFuncModEdit(true);
  };
  const transformFuncModForEdit = (item) => ({
    title: item["Module Name"] || "",
    description: item["Functional Requirement Description"] || "",
    priority: item["Priority / Dependency / Version"]?.split(" / ")[0] || "",
    sNo: item["S.No"],
  });
  const handleFuncModSave = (updatedItem) => {
    const getColorByPriority = (priority) => {
      switch (priority) {
        case "Critical":
        case "High":
          return "#f8d7da";
        case "Medium":
          return "#ffeeba";
        case "Low":
          return "#d4edda";
        default:
          return "#ffffff";
      }
    };
    const updatedWithColor = {
      ...funcModData.find((item) => item["S.No"] === updatedItem.sNo),
      "Module Name": updatedItem.title,
      "Functional Requirement Description": updatedItem.description,
      "Priority / Dependency / Version": updatedItem.priority,
      color: getColorByPriority(updatedItem.priority),
    };
    setFuncModData((prevData) =>
      prevData.map((mod) =>
        mod["S.No"] === updatedWithColor["S.No"] ? updatedWithColor : mod
      )
    );
    setOpenFuncModEdit(false);
  };
  const handleFuncModDelete = (itemId) => {
    const deletedMod = funcModData.find(d => d["S.No"] === itemId);
    const updatedFuncMods = funcModData.filter((d) => d["S.No"] !== itemId)
      .map((item, idx) => ({ ...item, ["S.No"]: idx + 1 }));
    const updatedSecReqs = securityReqData.filter(req => req["Module Name"] !== deletedMod["Module Name"])
      .map((item, idx) => ({ ...item, ["S.No"]: idx + 1 }));
    setFuncModData(updatedFuncMods);
    setSecurityReqData(updatedSecReqs);

  };


  //Security  Requirements - Edit Functionality
  const handleSecReqMenuClick = (event, index) => {
    setAnchorE2(event.currentTarget);
    setSelectedSecReq(securityReqData[index]);
  };
  const handleSecReqMenuClose = () => {
    setAnchorE2(null);
    setSelectedSecReqIndex(null);
  };
  const handleSecReqEdit = (item) => {
    setSelectedSecReq(transformSecReqForEdit(item));
    setOpenSecReqEdit(true);
  };
  const transformSecReqForEdit = (item) => {
    let raw = Array.isArray(item["Security Requirements"])
      ? item["Security Requirements"].join("\n")
      : item["Security Requirements"] || "";
    const formatted = raw
      .split("\n")
      .map((line) => {
        return line.trim().replace(/^[-•*]\s*/, "");
      })
      .join("\n");
    return {
      title: item["Module Name"] || "",
      requirements: formatted,
      sNo: item["S.No"],
    };
  };
  const handleSecReqSave = (updatedItem) => {
    setSecurityReqData((prevData) =>
      prevData.map((req) =>
        req["S.No"] === updatedItem.sNo
          ? {
            ...req,
            "Module Name": updatedItem.title,
            "Security Requirements": updatedItem.requirements,
          }
          : req
      )
    );
    setOpenSecReqEdit(false);
  };
  const handleSecReqDelete = (itemId) => {
    const deletedReq = securityReqData.find(d => d["S.No"] === itemId);
    const updatedSecReqs = securityReqData.filter((d) => d["S.No"] !== itemId)
      .map((item, idx) => ({ ...item, ["S.No"]: idx + 1 }));
    const updatedFuncMods = funcModData.filter(mod => mod["Module Name"] !== deletedReq["Module Name"])
      .map((item, idx) => ({ ...item, ["S.No"]: idx + 1 }));
    setSecurityReqData(updatedSecReqs);
    setFuncModData(updatedFuncMods);
  };



  //System Architectute - Edit Functionality
  const handleArchDescMenuClick = (event, index) => {
    setAnchorE3(event.currentTarget);
    setSelectedArchDesc(archDesignDescription);
  };
  const handleArchDescMenuClose = () => {
    setAnchorE3(null);
    setSelectedArchDescIndex(null);
  };
  const handleArchDescEdit = (item) => {
    setSelectedArchDesc(transformArchDescForEdit(item));
    setOpenArchDescEdit(true);
  };
  const transformArchDescForEdit = (item) =>
    Array.isArray(item)
      ? item.map((description, index) => ({
        sNo: index + 1,
        description: description || "",
      }))
      : [];
  const handleArchDescSave = (updatedItems) => {
    const updatedDescriptions = [...archDesignDescription];
    updatedItems.forEach((item) => {
      updatedDescriptions[item.sNo - 1] = item.description;
    });
    setArchDesignDescription(updatedDescriptions);
    setArchDesignData((prevData) => ({
      ...prevData,
      parsedArchitectureDesign: {
        ...prevData.parsedArchitectureDesign,
        "Accompanying Description": updatedDescriptions,
      },
    }));
    setOpenArchDescEdit(false);
  };

  const handleArchDescDelete = () => {
    setArchDesignDescription([]);
    setArchDesignData((prevData) => ({
      ...prevData,
      parsedArchitectureDesign: {
        ...prevData.parsedArchitectureDesign,
        "Accompanying Description": [],
      },
    }));
  };


  //Colour based on Security Impact
  const getPriorityColor = (Priority) => {
    switch (Priority) {
      case "High":
        return "#FFB6C1";
      case "Low":
        return "#CCFFCC";
      case "Medium":
        return "#FFE5B4";
      case "Critical":
        return "#FF4500";
      default:
        return "#e0e0e0";
    }
  };


  //Handling function for like and dislike
  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };


  //Modal open, close
  const handleOpen = () => {
    setIsModalOpen(true);
  };
  const handleClose = () => {
    setIsModalOpen(false);
  };


  //Image Download
  const handleDownloadImage = () => {
    const flowchartImage = archDesignData?.flowchartResponse;
    if (flowchartImage) {
      const link = document.createElement("a");
      link.href = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(flowchartImage)}`;
      link.download = "system architetcure.svg";
      link.click();
    }
  };


  //Handling function for Design phase
  const handleDesignPhase = async () => {
    let progressHandler;
    progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 95000 });
    try {
      setSecurityLoaderState("Design Phase");
      try {

        progressHandler.start("Generating Security Recommendations");

        const secRecommendationsResponse = await secRecommendations({
          fmInput: funcModData,
          secReq: securityReqData,
        });
        const securityRecommendations = secRecommendationsResponse.data;

        progressHandler.updateMessage("Generating Threat Modelling");

        const threatModellingResponse = await fmTothreatModelling({
          funcMod: funcModData,
          securityReq: securityReqData,
          securityRecom: securityRecommendations,
          archDesign: archDesignData.parsedArchitectureDesign,
        });
        const threatModelling = threatModellingResponse.data;

        progressHandler.updateMessage("Generating Architecture Design Review");

        const addReviewResponse = await addToaddReview({
          add: archDesignData.parsedArchitectureDesign,
        });
        const addReview = addReviewResponse.data;

        await progressHandler.finishAndClose();

        navigate("/dashboard/security-testing/design-phase-output", {
          state: {
            securityRecommendations,
            threatModelling,
            addReview,
            archDesign: archDesignData,
            funcMod: funcModData,
            securityReq: securityReqData,
          },
        });
      } catch (error) {
        setLoadingState({ open: false, message: "", progress: null });
        console.error("Error occurred:", error);
      }
    } catch (error) {
      console.error("Error during design phase:", error);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setSecurityLoaderState(null);
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };


  //Rendering fucntion for each tab
  const getAccordionContent = () => {
    if (tabValue === 0) {
      return (
        <Box sx={{ padding: 1 }}>
          {funcModData.map((mod, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 1,
                backgroundColor: "#7E83880F",
                boxShadow: 1,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: index !== funcModData.length - 1 ? 1 : 0 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" fontWeight={550}>
                    {`${mod["S.No"]}. ${mod["Module Name"]}`}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Security Impact" arrow>
                    <Chip
                      label={
                        mod["Priority / Dependency / Version"] &&
                        mod["Priority / Dependency / Version"].split(" / ")[0]
                      }
                      sx={{
                        fontSize: "0.7rem",
                        color: "black",
                        fontWeight: "normal",
                        borderRadius: "3px",
                        backgroundColor: getPriorityColor(
                          mod["Priority / Dependency / Version"]?.split(
                            " / "
                          )[0]
                        ),
                        height: 25,
                        width: 70,
                        padding: "2px 2px",
                        textAlign: "center",
                      }}
                    />
                  </Tooltip>
                  <IconButton onClick={(e) => handleFuncModMenuClick(e, index)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Typography variant="body2">
                {`${mod["Functional Requirement Description"]}`}
              </Typography>
            </Box>
          ))}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleFuncModMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                handleFuncModEdit(selectedFuncMod);
                handleFuncModMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EditOutlinedIcon color="primary" />
                <span>Edit</span>
              </Stack>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleFuncModDelete(selectedFuncMod?.["S.No"]);
                handleFuncModMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <DeleteIcon color="error" />
                <span>Delete</span>
              </Stack>
            </MenuItem>

          </Menu>
        </Box>
      );
    } else if (tabValue === 1) {
      return (
        <Box sx={{ padding: 1 }}>
          {securityReqData.map((req, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: "#7E83880F",
                borderRadius: 1,
              }}
            >   <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", ml: 1 }}>
                <Typography variant="subtitle2" fontWeight={550}>
                  {req["Module Name"]}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => handleSecReqMenuClick(e, index)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              {Array.isArray(req["Security Requirements"]) ? (
                req["Security Requirements"].map((requirement, reqIndex) => {
                  const requirementStr = String(requirement).trim();
                  const requirementText = requirementStr.startsWith("- ")
                    ? requirementStr.substring(2).trim()
                    : requirementStr;
                  return (
                    <Typography
                      key={reqIndex}
                      variant="body2"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <li
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "0.5rem",
                        }}
                      >
                        <ArrowRightIcon fontSize="small" />
                        <span>{requirementText}</span>
                      </li>
                    </Typography>

                  );
                })
              ) : typeof req["Security Requirements"] === "string" ? (
                req["Security Requirements"]
                  .split("\n")
                  .map((requirement, reqIndex) => {
                    const requirementStr = String(requirement).trim();
                    const requirementText = requirementStr.startsWith("- ")
                      ? requirementStr.substring(2).trim()
                      : requirementStr;
                    return (
                      <Typography
                        key={reqIndex}
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        <li
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.5rem",
                          }}
                        >
                          <ArrowRightIcon fontSize="small" />
                          <span>{requirementText}</span>
                        </li>
                      </Typography>
                    );
                  })
              ) : (
                <Typography variant="body2" color="error">
                  Error: Security Requirements are in an unexpected format.
                </Typography>
              )}
            </Box>
          ))}
          <Menu
            anchorEl={anchorE2}
            open={Boolean(anchorE2)}
            onClose={handleSecReqMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                handleSecReqEdit(selectedSecReq);
                handleSecReqMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EditOutlinedIcon color="primary" />
                <span>Edit</span>
              </Stack>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleSecReqDelete(selectedSecReq?.["S.No"]);
                handleSecReqMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <DeleteIcon color="error" />
                <span>Delete</span>
              </Stack>
            </MenuItem>

          </Menu>
        </Box>
      );
    } else if (tabValue === 2) {
      return (
        <Box sx={{ padding: 1 }}>
          {archDesignData ? (
            <>
              <Box>
                {archDesignData.flowchartResponse && (
                  <Box sx={{ position: "relative", display: "inline-block" }}>
                    <img
                      src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                        archDesignData.flowchartResponse
                      )}`}
                      alt="Flowchart"
                      loading="lazy"
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                        transform: `scale(${zoomLevel})`,
                        border: "1px solid #ccc",
                        borderRadius: "10px",
                        padding: "5px",
                        display: "block",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        right: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        bgcolor: "rgba(128, 128, 128, 0.8)",
                        borderRadius: 1,
                        p: 0.5,
                      }}
                    >
                      <FileDownloadOutlinedIcon
                        style={{ cursor: "pointer", color: "#ffffff" }}
                        onClick={handleDownloadImage}
                      />
                      <ZoomInIcon
                        onClick={handleOpen}
                        style={{ cursor: "pointer", color: "#ffffff" }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              {archDesignDescription && archDesignDescription.length > 0 && (
                <Box sx={{ mb: 2, mt: 3, p: 1, backgroundColor: "#7E83880F", boxShadow: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="subtitle1" fontWeight={550} sx={{ mb: -2, ml: 1 }}>
                      Description
                    </Typography>
                    <IconButton onClick={(e) => handleArchDescMenuClick(e)}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2">
                    <ul style={{ paddingLeft: "0", lineHeight: "1.8", listStyle: "none" }}>

                      {Array.isArray(archDesignDescription) && archDesignDescription.map((point, index) => (
                        <li key={index} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
                          <ArrowRightIcon fontSize="small" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </Typography>
                </Box>
              )}

            </>
          ) : (
            <Typography variant="body2" color="info">
              No Architecture Design available.
            </Typography>
          )}
          <Menu
            anchorEl={anchorE3}
            open={Boolean(anchorE3)}
            onClose={handleArchDescMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem
              onClick={() => {
                handleArchDescEdit(selectedArchDesc);
                handleArchDescMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <EditOutlinedIcon color="primary" />
                <span>Edit</span>
              </Stack>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleArchDescDelete(selectedArchDesc["Accompanying Description"]);
                handleArchDescMenuClose();
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <DeleteIcon color="error" />
                <span>Delete</span>
              </Stack>
            </MenuItem>
          </Menu>
        </Box>
      );
    }
  };

  <Button
    label="Download"
    variant="text"
    onClick={handleDownload}
    startIcon={<FileDownloadOutlinedIcon />}
    sx={{ textTransform: "none" }}
  />;

  const accordionButtons = [
    {
      label: "Download All",
      startIcon: <FileDownloadOutlinedIcon />,
      variant: "text",
      onClick: handleDownload,
      sx: { textTransform: "none", color: "#49A3FF", float: "left" },
    },
    {
      label: "Download Final BRD",
      startIcon: <FileDownloadOutlinedIcon />,
      variant: "text",
      onClick: handleBRDDownload,
      sx: { textTransform: "none", color: "#49A3FF", float: "left" },
    },
    {
      label: "Next",
      startIcon: <GenerateButton />,
      onClick: handleDesignPhase,
      variant: "contained",
      sx: {
        textTransform: "none",
        backgroundColor: "#49A3FF",
        color: "#fff",
        marginLeft: "auto",
        float: "right",
      },
    },
  ];

  return (
    <>
      <CommonAccordion
        summary="Identified Components"
        details={
          <Box sx={{ mt: 0, pt: 0 }}>
            <Box
              sx={{
                maxHeight: "500px",
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  backgroundColor: "white",
                  px: 1,
                  py: 1,
                }}
              >
                <Tabs
                  value={tabValue}
                  onChange={(_, newValue) => setTabValue(newValue)}
                >
                  <Tab
                    label="Functional Modules"
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      fontSize: "0.90rem",
                      mr: 1,
                    }}
                  />
                  <Tab
                    label="Security Requirements"
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      fontSize: "0.90rem",
                      mr: 1,
                    }}
                  />
                  <Tab
                    label="System Architecture"
                    sx={{ flex: 1, textTransform: "none", fontSize: "0.90rem" }}
                  />
                </Tabs>
              </Box>
              <Box
                ref={scrollRef}
                sx={{
                  maxHeight: 400,
                  overflowY: "auto",
                  p: 1,
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  "-ms-overflow-style": "none",
                  "scrollbar-width": "none",
                }}
              >
                {getAccordionContent()}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Like" arrow>
                    <Box
                      onClick={() => handleThumbClick("like")}
                      sx={{
                        cursor: "pointer",
                        marginRight: 2,
                        "&:hover": { color: "#49A3FF" },
                      }}
                    >
                      {activeThumb === "like" ? (
                        <ThumbUpIcon sx={{ fontSize: "24px" }} />
                      ) : (
                        <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Tooltip title="Dislike" arrow>
                    <Box
                      onClick={() => handleThumbClick("dislike")}
                      sx={{
                        cursor: "pointer",
                        marginRight: 2,
                        "&:hover": { color: "#FF4842" },
                      }}
                    >
                      {activeThumb === "dislike" ? (
                        <ThumbDownIcon sx={{ fontSize: "24px" }} />
                      ) : (
                        <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
                      )}
                    </Box>
                  </Tooltip>

                  {tabValue === 0 ? (
                    <CommonCopy contentToCopy={funcModData} />
                  ) : tabValue === 1 ? (
                    <CommonCopy contentToCopy={securityReqData} />
                  ) : (
                    <CommonCopy contentToCopy={archDescriptionToDownload.data} />
                  )}
                </Box>

                <Button
                  variant="text"
                  onClick={handleEachTabDownload}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{
                    textTransform: "none",
                    color: "#42a5f5",
                    mr: 4,
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Box>
        }
        buttons={accordionButtons}
        defaultExpanded={true}
        fullScreenMode={true}
      />
      <CommonDownload
        open={downloadETDOpen}
        onClose={() => setDownloadETDOpen(false)}
        serviceName={downloadEachTabData?.serviceName}
        serviceResponse={downloadEachTabData?.serviceResponse}
        wordData={downloadEachTabData?.wordData}
        documents={["docx", "pdf"]}
      />
      <CommonDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        serviceName="Requirement Phase Document"
        serviceResponse={[
          {
            heading: "Functional Modules",
            type: "text",
            data: hrForPdf
          },
          ...functionalModulesToDownload,
          {
            heading: "Security Requirements",
            type: "text",
            data: hrForPdf
          },
          ...secRequirementsToDownload,
          {
            heading: "System Architecture",
            type: "text",
            data: hrForPdf
          },
          {
            heading: "",
            type: "image",
            data: pngData
          },
          archDescriptionToDownload

        ]}

        wordData={[
          {
            heading: "Functional Modules",
            type: "text",
            data: hrForWord
          },
          ...functionalModulesToDownload,
          {
            heading: "Security Requirements",
            type: "text",
            data: hrForWord
          },
          ...secRequirementsToDownload,
          {
            heading: "System Architecture",
            type: "text",
            data: hrForWord
          },
          {
            heading: "",
            type: "image",
            data: pngData?.replace(/^data:image\/png;base64,/, "") || ""

          },
          archDescriptionToDownload
        ]}
        documents={["docx", "pdf"]}
      />

      <CommonDownload
        open={downloadBRDOpen}
        onClose={() => setDownloadBRDOpen(false)}
        serviceName="Business Requirement Document"
        serviceResponse={[
          {
            heading: "Introduction",
            type: "text",
            data: formatSection(brdData["Introduction"])
          },
          {
            heading: "User Roles",
            type: "text",
            data: formatSection(brdData["User Roles"])
          },
          {
            heading: "System Perspective",
            type: "text",
            data: formatSection(brdData["System Perspective"])
          },
          {
            heading: "Business Process Overview",
            type: "text",
            data: formatSection(brdData["Business Process Overview"])
          },
          {
            heading: "Business Requirements",
            type: "text",
            data: formatSection(brdData["Business Requirements"])
          },
          {
            heading: "Appendices",
            type: "text",
            data: formatSection(brdData["Appendices"])
          }
        ]}

        wordData={[
          {
            heading: "Introduction",
            type: "text",
            data: formatSection(brdData["Introduction"])
          },
          {
            heading: "User Roles",
            type: "text",
            data: formatSection(brdData["User Roles"])
          },
          {
            heading: "System Perspective",
            type: "text",
            data: formatSection(brdData["System Perspective"])
          },
          {
            heading: "Business Process Overview",
            type: "text",
            data: formatSection(brdData["Business Process Overview"])
          },
          {
            heading: "Business Requirements",
            type: "text",
            data: formatSection(brdData["Business Requirements"])
          },
          {
            heading: "Appendices",
            type: "text",
            data: formatSection(brdData["Appendices"])
          }
        ]}
        documents={["docx", "pdf"]}
      />

      <CommonEditDialog
        open={openFuncModEdit}
        onClose={() => setOpenFuncModEdit(false)}
        onSave={handleFuncModSave}
        title="Edit Functional Modules"
        initialData={selectedFuncMod}
        fields={[
          { label: "Module", field: "title" },
          {
            label: "Security Impact",
            field: "priority",
            select: true,
            options: ["Critical", "High", "Medium", "Low"],
          },
          { label: "Description", field: "description", multiline: true, rows: 4 },
        ]}
      />

      <CommonEditDialog
        open={openSecReqEdit}
        onClose={() => setOpenSecReqEdit(false)}
        onSave={handleSecReqSave}
        title="Edit Security Requirements"
        initialData={selectedSecReq}
        fields={[
          { label: "Module", field: "title" },
          {
            label: "Security Requirements",
            field: "requirements",
            multiline: true,
            rows: 6,
          },
        ]}
      />
      {openArchDescEdit && Array.isArray(selectedArchDesc) && (
        <CommonEditDialog
          open={openArchDescEdit}
          onClose={() => setOpenArchDescEdit(false)}
          onSave={handleArchDescSave}
          title="Edit System Architecture Description"
          initialData={{
            description: selectedArchDesc
              .map((d) => {
                const trimmed = d.description?.trim() || "";
                return trimmed.replace(/^[-•*]\s*/, "");
              })
              .join("\n"),
          }}
          fields={[
            {
              label: "Description",
              field: "description",
              multiline: true,
              rows: 10,
            },
          ]}
          formatter={(data) =>
            data.description
              .split("\n")
              .map((line, idx) => ({
                sNo: idx + 1,
                description: line.trim().replace(/^[-•*]\s*/, ""),
              }))
              .filter((d) => d.description)
          }
        />

      )}

      <Modal
        open={isModalOpen}
        onClose={handleClose}
        aria-labelledby="zoomed-modal-title"
        aria-describedby="zoomed-modal-description"
        closeAfterTransition
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90vw",
            height: "90vh",
            bgcolor: "#fff",
            border: "none",
            boxShadow: 24,
            borderRadius: 2,
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#000",
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>

          <img
            src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
              archDesignData.flowchartResponse
            )}`}
            alt="Flowchart"
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              border: "none",
              borderRadius: "8px",
            }}
          />
        </Box>
      </Modal>
    </>
  );
};

export default IdentifiedFunctionalModules;
